import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { EmployeeViewModel } from '@trinnvis/dabih-api-client';
import { getStore } from '../../store/index.js';
import { updateChatExpanded } from '../../store/actions/actions.js';
import { reduxConnectMixin } from '../../redux-behavior.js';
import type { State } from '../../store/types.js';
import { chatLog } from '../../utilities/chat-log.js';

/**
 * Floating chat window container displayed in bottom-right corner.
 * Contains a React root for rendering d-chat-viewer.
 * Expands from chat bubble with animation.
 */
@customElement('d-chat-window')
export class DChatWindow extends reduxConnectMixin(LitElement) {
  static readonly styles = css`
    :host {
      position: fixed;
      bottom: 100px;
      right: 20px;
      z-index: 400;
      display: block;
      opacity: 0;
      transform: scale(0.8) translateY(20px);
      transition:
        opacity 0.3s,
        transform 0.3s;
      pointer-events: none;
    }

    :host([open]) {
      opacity: 1;
      transform: scale(1) translateY(0);
      pointer-events: auto;
    }

    :host([is-app-platform]) {
      bottom: 160px;
    }

    @media only screen and (min-width: 845px) {
      :host {
        bottom: 110px;
        right: 30px;
      }

      :host([is-app-platform]) {
        bottom: 170px;
      }
    }

    @media only screen and (max-width: 844px) {
      :host([open]) {
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100vw;
        height: 100vh;
        transform: none;
      }
    }

    #container {
      box-sizing: border-box;
      width: 420px;
      height: 680px;
      background: white;
      border-radius: 12px;
      box-shadow: 0 16px 24px 2px hsla(0, 0%, 0%, 0.3);
      display: flex;
      flex-direction: column;
      overflow: hidden;
      transition: width 0.3s ease;
    }

    :host([expanded]) #container {
      width: 820px;
    }

    @media only screen and (max-width: 1200px) {
      :host([expanded]) #container {
        width: 90vw;
        max-width: 820px;
      }
    }

    @media only screen and (max-width: 844px) {
      #container {
        width: 100%;
        height: 100%;
        border-radius: 0;
        /* Fullskjerm på mobil: hold header klar av safe-area (notch/statusbar)
           oppe, og input klar av safe-area/hjemindikator nede. */
        padding-top: var(--safe-area-inset-top, 0px);
        padding-bottom: var(--safe-area-inset-bottom, 0px);
      }

      /* På app-plattform flyter mobil-navigasjonen (hurtigmeny + forrige/neste,
         d-mobile-app-navigation) nederst på bottom: 20px, høyde 40px. Reserver
         plass slik at meldingsinput ikke blir dekket av disse knappene. */
      :host([is-app-platform]) #container {
        padding-bottom: calc(var(--safe-area-inset-bottom, 0px) + 72px);
      }

      :host([expanded]) #container {
        width: 100%;
      }
    }

    #content {
      flex: 1;
      overflow: hidden;
      position: relative;
    }

    #root {
      width: 100%;
      height: 100%;
    }
  `;

  @property({ type: Boolean, reflect: true })
  open = false;

  @property({ type: Boolean, attribute: 'is-app-platform', reflect: true })
  isAppPlatform = false;

  @property({ type: String })
  mode: 'ai' | 'user' = 'ai';

  @property({ type: Number })
  organizationId = 0;

  @property({ type: String })
  organizationName = '';

  @property({ type: String })
  currentUserName = '';

  @property({ type: String })
  currentUserUuid = '';

  // Innlogget brukers e-post (state.username). Føres videre til d-chat-viewer
  // som gater «Assistent»-oppføringen til utvalgte brukere under utrulling.
  @property({ type: String })
  currentUserEmail = '';

  @property({ type: Array })
  employees: EmployeeViewModel[] = [];

  // KI-flagget (ASK). Agent-boten i chaten krever både intern chat (MATRIX) og
  // KI (ASK); føres videre til d-chat-viewer som gater «Assistent»-oppføringen.
  @property({ type: Boolean, attribute: 'ai-enabled' })
  aiEnabled = false;

  @property({ type: Boolean, reflect: true, attribute: 'expanded' })
  expanded = false;

  stateChanged(state: State) {
    this.expanded = state.chatExpanded;
  }

  connectedCallback() {
    super.connectedCallback();
    chatLog('[d-chat-window] Connected, adding event listener');
    this.addEventListener('chat-expanded-change', this.onExpandedChange as EventListener);
    // Hele chat-treet (d-chat-viewer med React-chat-UI, matrix-js-sdk,
    // emoji-picker m.m.) ligger i egen chunk og lastes først når chat-vinduet
    // faktisk kobles til (#13442). Lit oppgraderer <d-chat-viewer>-elementet i
    // templaten når modulen er lastet, og matrix-init (unread-tellere m.m.)
    // starter som før fra d-chat-viewers egen livssyklus.
    void import('../../outskirts/d-chat-viewer.js');
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('chat-expanded-change', this.onExpandedChange as EventListener);
  }

  render() {
    return html`
      <div id="container">
        <d-chat-viewer
          .organizationId=${this.organizationId}
          .organizationName=${this.organizationName}
          .currentUserName=${this.currentUserName}
          .currentUserUuid=${this.currentUserUuid}
          .currentUserEmail=${this.currentUserEmail}
          .employees=${this.employees}
          ?ai-enabled=${this.aiEnabled}
          ?expanded=${this.expanded}
          ?chat-open=${this.open}
          @close-chat=${this.onClose}
        ></d-chat-viewer>
      </div>
    `;
  }

  private onClose() {
    this.dispatchEvent(new CustomEvent('close-chat', { composed: true, bubbles: true }));
  }

  private onExpandedChange(e: CustomEvent<{ expanded: boolean }>) {
    chatLog('[d-chat-window] Received expanded change event:', e.detail.expanded);
    // Dispatch to Redux instead of setting local state
    getStore().dispatch(updateChatExpanded(e.detail.expanded));
    chatLog('[d-chat-window] Dispatched updateChatExpanded to Redux:', e.detail.expanded);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-chat-window': DChatWindow;
  }
}
